import { faCog, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import BlogsImageDetail from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail";
import {
    blogsImageDetailDeleteImageTrigger,
    blogsImageDetailFetchTrigger,
    blogsImageDetailRotateImageTrigger,
    blogsImageDetailSaveImageTitleTrigger,
    blogsImageDetailTitleEdit,
} from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.actions";
import {
    BlogsImageDetailImageState,
    BlogsImageDetailPostState,
    blogsImageDetailsReducer,
} from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.reducers";
import blogsImageDetailsSaga from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.sagas";
import {
    getBlogsImageDetailImageState,
    getBlogsImageDetailPostState,
} from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetail.selectors";
import blogsSharedSaga from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import {
    getCommentIdsOrNull,
    getPostsState,
} from "@/mk2/apps/blogs/containers/Comments/Comments.selectors";
import { findBlogPost } from "@/mk2/apps/blogs/selectors";
import { imageBlogsOG } from "@/mk2/apps/blogs/structuredData";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { BlogPostEntity, ImageEntity, ImageSchema, MenuSelection, UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    AUTOMATIC_COMMENT_VIOLATES_RULES,
    SEO_TITLE_BLOGS_SHORT_MESSAGE_OF_username,
} from "@/mk/autogenerated/translation";
import { isAuthenticated, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: "lg" };

export const BLOGS_IMAGE_DETAIL_MENU_MORE = "BLOGS_IMAGE_DETAIL_MENU_MORE";

// aby sme vedeli rozlisit medzi image detailami pre short message a album uz pocas
// LoadingState.INIT a LoadingState.LOADING a zobrazovali tak spravne UI
export enum BlogsImageDetailViewType {
    ShortMessage,
    Album,
}

export interface OwnProps {}

export interface StateProps {
    viewType: BlogsImageDetailViewType;
    actionLoadingState: LoadingState;
    baseUrl: string;
    commentsCount: number;
    currentImage: ImageEntity;
    currentIndex: number;
    errorMessage: string;
    isEditingTitle: boolean;
    isTiedToPost: boolean;
    loadingState: LoadingState;
    post: BlogPostEntity;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    defaultCanAdd: boolean;
}

export interface DispatchProps {
    onDeleteImage(username: string, postSlug: string, imageId: number);
    onLoad(username: string, postSlug: string, imageId: number);
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onRotateImage(username: string, postSlug: string, imageId: number, angle: number);
    onSaveTitle(username: string, postSlug: string, imageId: number, title: string);
    onTitleEdit(username: string, postSlug: string, imageId: number, enabled: boolean);
    onToggleMenuMore();
}

export type RouteProps = PageRouteProps<{
    username: string;
    postSlug: string;
    imageId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export abstract class BlogsImageDetailPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: { params },
            } = ownProps;
            const username = params.username;
            const postSlug = params.postSlug;
            const imageId = parseInt(params.imageId, 10);

            const postState: BlogsImageDetailPostState = getBlogsImageDetailPostState(
                state,
                username,
                postSlug,
            );
            const imageState: BlogsImageDetailImageState = getBlogsImageDetailImageState(
                state,
                username,
                postSlug,
                imageId,
            );
            const currentImage = getDenormalizedEntity<ImageEntity>(state, ImageSchema, imageId);

            const post = findBlogPost(state, postSlug);

            const currentIndex = post ? post.images.findIndex((image) => image.id === imageId) : 0;
            const commentsLoadingState = imageState.commentsLoadingState;

            let commentsCount = 0;
            if (post && currentImage) {
                const key = `image/${imageId}`;
                const commentIdsOrNull = getCommentIdsOrNull(getPostsState(state), key);
                commentsCount =
                    commentIdsOrNull !== null && commentsLoadingState !== LoadingState.LOADING
                        ? commentIdsOrNull.length
                        : currentImage.commentsCount;
            }

            return {
                viewType: undefined, // musi definovat subclass
                actionLoadingState: imageState.actionLoadingState,
                baseUrl: getRequestBaseUrl(state),
                commentsCount,
                currentImage,
                currentIndex,
                errorMessage: imageState.errorMessage,
                isEditingTitle: imageState.isEditingTitle,
                isTiedToPost: imageState.isTiedToPost,
                loadingState: imageState.loadingState,
                post,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                isMobile: getRequestDeviceMobile(state),
                defaultCanAdd: postState.defaultCanAdd,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onDeleteImage: blogsImageDetailDeleteImageTrigger,
            onLoad: blogsImageDetailFetchTrigger,
            onReport: complaintOpenTrigger,
            onRotateImage: blogsImageDetailRotateImageTrigger,
            onSaveTitle: blogsImageDetailSaveImageTitleTrigger,
            onTitleEdit: blogsImageDetailTitleEdit,
            onToggleMenuMore: () => menuToggle(BLOGS_IMAGE_DETAIL_MENU_MORE),
        };
    }

    public getMetaTitle(props: Props): string {
        const {
            currentImage,
            post,
            match: {
                params: { username },
            },
        } = props;
        // Use image title, or perex content if available
        const contentText =
            currentImage?.title ||
            (post?.perexAST ? getFirstParagraphsPlaintext(post.perexAST) : null);
        if (contentText) {
            const prefix = `@${username}: `;
            const maxContentLength = 120 - prefix.length;
            const truncatedContent =
                contentText.length > maxContentLength
                    ? contentText.substring(0, maxContentLength - 3) + "..."
                    : contentText;
            return prefix + truncatedContent;
        }
        return interpolate(SEO_TITLE_BLOGS_SHORT_MESSAGE_OF_username, { username });
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { post, currentImage } = props;
        if (post && currentImage) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...imageBlogsOG(baseUrl, post, currentImage),
            };
        }
    }

    public render(props: Props): false | JSX.Element {
        return <BlogsImageDetail {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Blogs Feed - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile ? "Blogs Post - Top 01 - Mobile" : "Blogs - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: props.isMobile ? "Blogs Post - Interstitial" : "Blogs - Interstitial" };
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { requestUser, viewType, onToggleMenuMore, currentImage, post, onReport } = props;

        const showReport =
            post &&
            !post.deletedAt &&
            viewType === BlogsImageDetailViewType.Album &&
            !isAuthenticatedAs(requestUser, post.author);

        return (
            isAuthenticated(requestUser) &&
            showReport && (
                <Menu
                    key="menu"
                    menuId={BLOGS_IMAGE_DETAIL_MENU_MORE}
                    trigger={
                        <Btn
                            type={BtnType.BlueOutline}
                            icon={fasCogLg}
                            onClick={onToggleMenuMore}
                        />
                    }
                >
                    {showReport && (
                        <MenuItem
                            label={AUTOMATIC_COMMENT_VIOLATES_RULES}
                            icon={faExclamationTriangle}
                            onClick={this.handleOnReport(onReport, currentImage.id)}
                        />
                    )}
                </Menu>
            )
        );
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "photoblog",
            activeItem: null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.blogs.blogsImageDetails", blogsImageDetailsReducer);
        store.injectSaga("containers.blogs.BlogsImageDetails", blogsImageDetailsSaga);

        store.injectSaga("containers.blogs.blogsShared", blogsSharedSaga);
        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }

    private handleOnReport = (onReport, imageId) => () => {
        onReport(ComplaintTargetType.PhotoblogImage, imageId);
    };
}
